import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, Plus, Trash, PlugsConnected, ArrowClockwise, XCircle } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { EmptyState } from "@/promotions/components/EmptyState";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { CardsSkeleton } from "@/promotions/components/Skeleton";
import { ConfirmDialog } from "@/promotions/components/campaign/ConfirmDialog";
import { apiError } from "@/lib/api";
import { Modal, Field, TextInput } from "../components/Modal";
import { useCreativeData, creativeApi } from "../useCreative";
import { fmtDateTime } from "../creativeUtils";

const PROVIDERS = [
  { value: "social.facebook", label: "Facebook (simulated)" },
  { value: "social.instagram", label: "Instagram (simulated)" },
  { value: "social.meta.facebook", label: "Facebook (Meta — live)" },
  { value: "social.meta.instagram", label: "Instagram (Meta — live)" },
  { value: "social.linkedin", label: "LinkedIn" },
  { value: "social.pinterest", label: "Pinterest" },
  { value: "social.x", label: "X (Twitter)" },
];
const DEST_TYPES = ["account", "page", "channel", "board", "company_page", "business_account", "profile", "other"];
const EMPTY = { provider: "social.facebook", connection_id: "", destination_id: "", destination_name: "", destination_type: "page" };

export default function Publishing() {
  const { destinations, publishRequests, assets, loading, reload } = useCreativeData();
  const [adding, setAdding] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toCancel, setToCancel] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const assetById = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a])), [assets]);
  const requests = useMemo(
    () => publishRequests.filter((r) => !statusFilter || r.status === statusFilter),
    [publishRequests, statusFilter],
  );

  const save = async () => {
    if (!adding.connection_id.trim() || !adding.destination_id.trim()) return toast.error("Connection and destination id are required");
    setSaving(true);
    try {
      await creativeApi.createDestination({
        provider: adding.provider,
        connection_id: adding.connection_id.trim(),
        destination_id: adding.destination_id.trim(),
        destination_name: adding.destination_name.trim() || adding.destination_id.trim(),
        destination_type: adding.destination_type,
      });
      toast.success("Destination added");
      setAdding(null);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try { await creativeApi.deleteDestination(toDelete.id); toast.success("Destination removed"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setToDelete(null); }
  };

  const confirmCancel = async () => {
    try { await creativeApi.cancelPublishRequest(toCancel.id); toast.success("Publish request cancelled"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setToCancel(null); }
  };

  return (
    <div className="space-y-10" data-testid="creative-publishing">
      <PromoPageHeader
        overline="Creative Studio"
        title="Publishing"
        description="Publishing destinations and the request queue handed off to Social. Creative never publishes directly."
        actions={
          <button onClick={reload} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="publishing-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />

      {/* Destinations */}
      <section className="space-y-4" data-testid="destinations-section">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium tracking-tight">Destinations</h3>
          <button onClick={() => setAdding({ ...EMPTY })} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90" data-testid="destination-new">
            <Plus size={16} weight="bold" /> Add destination
          </button>
        </div>
        {loading ? (
          <CardsSkeleton count={3} />
        ) : destinations.length === 0 ? (
          <EmptyState Icon={PlugsConnected} title="No destinations yet" description="Add a publishing destination to send approved creative to Social." />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="destinations-grid">
            {destinations.map((d) => (
              <div key={d.id} className="flex items-start justify-between gap-2 rounded-lg border border-border bg-card p-5" data-testid={`destination-${d.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/10 text-brand"><PlugsConnected size={18} weight="duotone" /></div>
                  <div>
                    <div className="font-medium">{d.destination_name || d.destination_id}</div>
                    <div className="text-xs text-muted-foreground">{d.provider} · {d.destination_type}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={d.enabled === false ? "disconnected" : "active"} dot={false} label={d.enabled === false ? "disabled" : "enabled"} />
                  <button onClick={() => setToDelete(d)} aria-label="Remove destination" className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive" data-testid={`destination-delete-${d.id}`}>
                    <Trash size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Publishing queue & history */}
      <section className="space-y-4" data-testid="queue-section">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium tracking-tight">Publishing queue & history</h3>
          <PromoSelect
            value={statusFilter} onChange={setStatusFilter} placeholder="All statuses" className="w-44"
            options={[{ value: "", label: "All statuses" }, { value: "requested", label: "Requested" }, { value: "received", label: "Received" }, { value: "published", label: "Published" }, { value: "cancelled", label: "Cancelled" }, { value: "failed", label: "Failed" }]}
            testId="queue-filter-status"
          />
        </div>
        {requests.length === 0 ? (
          <EmptyState Icon={PaperPlaneTilt} title="No publish requests" description="Approve an asset and publish it to see requests here." />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="queue-table">
                <thead className="promo-glass border-b border-border">
                  <tr>{["Asset", "Destinations", "Status", "Requested", ""].map((h) => (<th key={h} className="px-4 py-3 text-left promo-overline whitespace-nowrap">{h}</th>))}</tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-accent/30" data-testid={`pubreq-row-${r.id}`}>
                      <td className="px-4 py-3 capitalize">{assetById[r.asset_id]?.operation || r.kind} · <span className="font-mono text-xs text-muted-foreground">{r.asset_id.slice(0, 8)}</span></td>
                      <td className="px-4 py-3 text-muted-foreground">{(r.destinations || []).map((d) => d.destination_name).join(", ") || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmtDateTime(r.requested_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {["requested", "received"].includes(r.status) && (
                          <button onClick={() => setToCancel(r)} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive" data-testid={`pubreq-cancel-${r.id}`}>
                            <XCircle size={13} /> Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Add destination dialog */}
      <Modal
        open={!!adding}
        onClose={() => setAdding(null)}
        title="Add publishing destination"
        description="A destination maps an existing OAuth connection to a publishable target (page/account/channel)."
        footer={
          <>
            <button onClick={() => setAdding(null)} className="rounded-md border border-border px-3.5 py-2 text-sm font-medium transition-colors hover:bg-accent" data-testid="destination-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60" data-testid="destination-save">{saving ? "Saving…" : "Add"}</button>
          </>
        }
      >
        {adding && (
          <div className="space-y-4">
            <Field label="Provider">
              <PromoSelect value={adding.provider} onChange={(v) => setAdding((s) => ({ ...s, provider: v }))} options={PROVIDERS} testId="destination-select-provider" />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Connection ID" hint="Existing OAuth connection">
                <TextInput value={adding.connection_id} onChange={(e) => setAdding((s) => ({ ...s, connection_id: e.target.value }))} placeholder="conn-123" data-testid="destination-input-connection" />
              </Field>
              <Field label="Destination ID" hint="Provider-native id (page/account/board)">
                <TextInput value={adding.destination_id} onChange={(e) => setAdding((s) => ({ ...s, destination_id: e.target.value }))} placeholder="ig-17841400000" data-testid="destination-input-destid" />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Display name">
                <TextInput value={adding.destination_name} onChange={(e) => setAdding((s) => ({ ...s, destination_name: e.target.value }))} placeholder="Brand Instagram" data-testid="destination-input-name" />
              </Field>
              <Field label="Type">
                <PromoSelect value={adding.destination_type} onChange={(v) => setAdding((s) => ({ ...s, destination_type: v }))} options={DEST_TYPES.map((t) => ({ value: t, label: t }))} testId="destination-select-type" />
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!toDelete} danger title="Remove destination?" description={toDelete ? `"${toDelete.destination_name || toDelete.destination_id}" will be removed.` : ""} confirmLabel="Remove" cancelLabel="Cancel" onConfirm={confirmDelete} onCancel={() => setToDelete(null)} />
      <ConfirmDialog open={!!toCancel} danger title="Cancel publish request?" description={toCancel ? `Request ${toCancel.id.slice(0, 8)} will be cancelled.` : ""} confirmLabel="Cancel request" cancelLabel="Keep" onConfirm={confirmCancel} onCancel={() => setToCancel(null)} />
    </div>
  );
}
